import React, { FC, Fragment, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { Formik, Form as FormikForm, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';

import {
  ActionContainer,
  ClientLogo,
  Error,
  Layouts,
  SubmitButton,
  ValidatedTextField,
  VisibilityAdornment,
} from 'components';
import { useError } from 'hooks';

import { IAuthContainerProps } from '../types';
import { INewPasswordValues, NewPasswordSchema } from '../validations';
import { Main } from '../components';

const RequireNewPassword: FC<IAuthContainerProps> = ({ authData }) => {
  const [visible, setVisible] = useState(false);

  const { t } = useTranslation();
  const { error, onError, onErrorClose } = useError();

  const initialValues: INewPasswordValues = {
    password: '',
  };

  async function handleSubmit(
    values: INewPasswordValues,
    actions: FormikHelpers<INewPasswordValues>,
  ) {
    try {
      await Auth.completeNewPassword(authData.user, values.password, {});
    } catch (error) {
      onError(error);
    } finally {
      actions.setSubmitting(false);
    }
  }

  function renderSidebar() {
    return (
      <Fragment>
        <ClientLogo />
        <Typography gutterBottom variant="h4" color="textSecondary">
          {t('auth:forgotPasswordSidebarHeader')}
        </Typography>
        <Typography variant="subtitle1">{t('auth:requirePasswordChange')}</Typography>
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={NewPasswordSchema}>
          {({ isSubmitting }) => (
            <FormikForm>
              <ValidatedTextField
                type={visible ? 'text' : 'password'}
                name="password"
                i18nKey="user:newPassword"
                InputProps={{
                  endAdornment: (
                    <VisibilityAdornment visible={visible} onToggle={() => setVisible(!visible)} />
                  ),
                }}
              />
              <ActionContainer>
                <SubmitButton working={isSubmitting} text={t('common:save')} />
              </ActionContainer>
            </FormikForm>
          )}
        </Formik>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Error key={error} message={error} onClose={onErrorClose} />
      <Layouts.Full2Column
        sidebar={renderSidebar()}
        main={<Main headerI18nKey="auth:bodyHeader1" subHeaderI18nKey="auth:signInSubHeader" />}
      />
    </Fragment>
  );
};

export default RequireNewPassword;
